import { Loader2, Printer } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PERMISSIONS as P, usePermission } from '@/lib/permissions'
import { usePreviewPickList, useRecordPickListPrint } from '@/hooks/usePickLists'
import { printPickSheet } from './printPickSheet'
import type { GeneratePickListPayload } from '@/types/pickList'

/**
 * Print this scope's pick sheet, as a single icon.
 *
 * The same action `PickListActionsMenu` offers, without the menu. On a list
 * page a menu earns its click — there are three things you might want from a
 * row. At the bench, working one process step, there is one: the sheet for what
 * you are about to fetch. Putting that behind a popover is a click that only
 * ever leads to the same place.
 *
 * Uses the PREVIEW path, like the menu does: printing must never persist a
 * `PickList` row, or every print would litter the index with sheets nobody
 * asked to keep.
 */
export function PrintPickListButton({
  scope,
  className,
  label = 'Print pick list',
}: {
  scope: GeneratePickListPayload
  className?: string
  /** Also the accessible name and the tooltip — an icon alone names nothing. */
  label?: string
}) {
  const { can } = usePermission()
  const preview = usePreviewPickList()
  const recordPrint = useRecordPickListPrint()

  // Same gate as the menu's print item. Hidden rather than disabled: a control
  // you may never use is noise, not information.
  if (!can(P.inventoryView) && !can(P.pickListsGenerate)) return null

  return (
    <>
      <button
        type='button'
        title={label}
        aria-label={label}
        disabled={preview.isPending}
        // The step card is clickable in places; printing must not also select
        // or navigate the row underneath.
        onClick={e => {
          e.stopPropagation()
          preview.mutate(scope, {
            onSuccess: pickList => {
              printPickSheet(pickList)
              // The sheet already being picked is what printed (z8nrz7fn5r):
              // its trail records it, as the pick list page's Print does.
              // Audit only, never awaited, and only with inventory.view, which
              // the route requires.
              if (pickList.id && can(P.inventoryView)) recordPrint.mutate(pickList.id)
            },
          })
        }}
        className={cn(
          'inline-flex h-9 w-9 items-center justify-center rounded-md border border-input',
          'text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
      >
        {preview.isPending ? (
          <Loader2 className='h-4 w-4 animate-spin' />
        ) : (
          <Printer className='h-4 w-4' />
        )}
      </button>
      {preview.error && (
        <p className='mt-1 text-[11px] font-medium text-destructive'>
          {preview.error instanceof Error
            ? preview.error.message
            : 'Could not build the pick sheet.'}
        </p>
      )}
    </>
  )
}
